<script>
  import { billingEnumMixins } from '@/mixins/billingEnumMixins';
  import { checkNumNA, isValid } from 'utils/number';
  import { checkStrNA, isBlank, isNotBlank, splitStr } from 'utils/string';
  import { userInfoMixin } from '@/mixins/userInfoMixin';
  import { validateAmount } from 'utils/validator';
  import { financeInfoMixins } from '@/mixins/financeInfoMixins';
  import LineFrame from '@/components/common/LineFrame/LineFrame.vue';
  import {
    confirmAdvReceiptClaim,
    doAdvReceiptClaim,
    getAdvReceiptClaimPreviewData,
  } from 'api/billing/advertiser/advReceiptClaimAPI';
  import { hasDuplicateProperties } from 'utils/array';

  const defaultFormData = {
    advReceiptClaimNumber: null,
    recordDateRange: [],
    advCompanyPlatform: null,
    advEntity: null,
    receiptType: null,
    hyperTapEntity: null,
    receiptStatus: null,
    receiptClaimStatus: null,
    paymentTime: null,
    pmConfirmRemark: null,
    pmAuditRemark: null,
    currency: 'CNY',
    advRevenue: null,
    advConfirmedAmount: null,
    advertiserDateItems: [
      {
        recordDate: null,
        advId: null,
      },
    ],
  };

  const defaultPreviewData = {
    checkMessage: '',
    advRevenue: null,
    advEntity: null,
    advCompanyPlatform: null,
    advConfirmedAmount: null,
    currency: null,
    advHedgeAmount: null,
    financeReceivedAmount: null,
    financeReceivableAmount: null,
    receiptDate: null,
    hyperTapEntity: null,
    advDetailList: [],
    fileList: [],
  };

  export default {
    name: 'AdvPaymentReceiptConfirmForm',
    components: { LineFrame },
    props: {
      receiptType: {
        type: String,
        default: 'POST_RECEIPT',
      },
      receiptClaimStatus: {
        type: String,
        default: '',
      },
      confirmDialogVisible: {
        type: Boolean,
        default: false,
      },
    },
    mixins: [billingEnumMixins, userInfoMixin, financeInfoMixins],
    data() {
      return {
        formData: Object.assign({}, defaultFormData),
        previewData: Object.assign({}, defaultPreviewData),
        rules: {
          advCompanyPlatform: [{ required: true, trigger: 'blur' }],
          advEntity: [{ required: true, trigger: 'blur' }],
          recordDateRange: [{ required: true, trigger: 'blur' }],
          hyperTapEntity: [{ required: true, trigger: 'blur' }],
          currency: [{ required: true, trigger: 'blur' }],
          advReceiptClaimNumber: [{ required: true, trigger: 'blur' }],
          paymentTime: [{ required: true, trigger: 'blur' }],
          financePaymentAmount: [{ validator: validateAmount, required: true, trigger: 'blur' }],
          remark: [],
          operationAuditRemark: [],
          advertiserDateItems: [],
          sendEmail: [],
        },
        formProcessing: false,
      };
    },
    computed: {
      computedAdvertiserDateItems: function() {
        return JSON.parse(JSON.stringify(this.formData));
      },
    },
    watch: {
      /**
       * 监听广告主和日期变化，重新获取预览数据
       */
      computedAdvertiserDateItems: {
        handler(newVal, oldVal) {
          const newAdvertiserDateItems = newVal.advertiserDateItems;
          const oldAdvertiserDateItems = oldVal.advertiserDateItems;
          const isEqual = newAdvertiserDateItems.every((item, index) => {
            // 避免空数据触发
            if (!isValid(item.advId)) {
              return true;
            }
            return (
              item.advId === oldAdvertiserDateItems[index].advId &&
              item.recordDate === oldAdvertiserDateItems[index].recordDate
            );
          });
          if (!isEqual) {
            setTimeout(() => {
              if (!this.formProcessing) {
                this.handleFetchDetail();
              }
            }, 500);
          }
        },
        deep: true,
      },
    },
    methods: {
      checkStrNA,
      splitStr,
      isNotBlank,
      checkNumNA,
      handleFormProcessingEnd() {
        setTimeout(() => {
          this.formProcessing = false;
        }, 500);
      },
      handleFetchDetail() {
        if (
          this.receiptClaimStatus === 'CLAIMED' &&
          (isBlank(this.formData.advReceiptClaimNumber) ||
            isBlank(this.formData.advCompanyPlatform) ||
            isBlank(this.formData.advEntity))
        ) {
          this.$message({
            message: '请先填写正确的申请单号 && 平台 && 实体',
            type: 'error',
          });
          return;
        }
        this.formProcessing = true;
        const advBfiId = this.specificCompanyEntity.find(
          (item) => item.companyEntity === this.formData.advEntity
        )?.id;
        getAdvReceiptClaimPreviewData({
          advReceiptClaimNumber: this.formData.advReceiptClaimNumber,
          advEntity: this.formData.advEntity,
          advCompanyPlatform: this.formData.advCompanyPlatform,
          advBfiId,
          advertiserItems: this.formData.advertiserDateItems
            .filter((item) => isValid(item.advId))
            .map((item) => ({
              advId: item.advId,
              recordDate: item.recordDate,
            })),
        })
          .then((res) => {
            if (res.result) {
              this.previewData.checkMessage = null;
              this.previewData = Object.assign(this.previewData, res.result);
              // 清理重复 advDetailList 元素
              this.previewData.advDetailList = this.previewData.advDetailList.filter(
                (item, index, arr) => {
                  return arr.findIndex((t) => t.advId === item.advId) === index;
                }
              );
              this.formData.receiptType = res.result.receiptType;
              this.formData.receiptStatus = res.result.receiptStatus;
            }
          })
          .catch(() => {})
          .finally(() => {
            this.handleFormProcessingEnd();
          });
      },
      handleSubmit(receiptClaimStatus) {
        this.$refs.baseForm.validate((valid) => {
          if (valid) {
            // 预收款收款状态固定
            if (this.receiptType === 'PRE_RECEIPT') {
              this.formData.receiptStatus = 'PRE_RECEIPT';
              // 预收认领无需指定广告主
              this.formData.advertiserDateItems = [];
            }
            this.formData.receiptType = this.receiptType;
            this.formData.receiptClaimStatus = this.receiptClaimStatus;
            this.formProcessing = true;
            // 判断是否存在重复的日期+广告主
            if (hasDuplicateProperties(this.formData.advertiserDateItems)) {
              this.$message({
                message: '存在重复的日期 && 广告主, 请检查修正后重试',
                type: 'error',
              });
              this.handleFormProcessingEnd();
              return;
            }

            // 清理无效 advertiserDateItems 元素
            this.formData.advertiserDateItems = this.formData.advertiserDateItems.filter((item) =>
              isValid(item.advId)
            );
            if (this.receiptClaimStatus === 'CLAIMED') {
              doAdvReceiptClaim({ ...this.formData, receiptClaimStatus })
                .then(() => {
                  this.$message({
                    message: 'Claim Success',
                    type: 'success',
                  });
                  this.$emit('refresh-list');
                  this.handleClose();
                })
                .finally(() => {
                  this.handleFormProcessingEnd();
                });
            }
            if (this.receiptClaimStatus === 'CLAIM_CONFIRMED') {
              confirmAdvReceiptClaim({ ...this.formData, receiptClaimStatus })
                .then(() => {
                  this.$message({
                    message: 'Claim Success',
                    type: 'success',
                  });
                  this.$emit('refresh-list');
                  this.handleClose();
                })
                .finally(() => {
                  this.handleFormProcessingEnd();
                });
            }
          } else {
            return false;
          }
        });
      },
      handleClose() {
        this.$emit('update:confirmDialogVisible', false);
        this.$emit('update:receiptClaimStatus', 'null');
        this.$emit('update:receiptType', 'POST_RECEIPT');
        setTimeout(() => {
          this.$refs.baseForm.resetFields();
          this.formData = Object.assign({}, defaultFormData);
          this.formData.advertiserDateItems = [
            {
              recordDate: null,
              advId: null,
            },
          ];
          this.previewData = Object.assign({}, defaultPreviewData);
          this.checkMessage = '';
        }, 300);
      },
      handleAdvertiserDateItem(index, isRemove) {
        if (isRemove) {
          this.formData.advertiserDateItems.splice(index, 1);
        } else {
          this.formData.advertiserDateItems.push({
            recordDate: null,
            advId: null,
          });
        }
      },
      /**
       * 从广告主预览数据提取BD/PM
       */
      getPmBdNameFromAdvList(advId, isPm) {
        if (!isValid(advId)) {
          return;
        }
        const advInfo = this.previewData.advDetailList.find((adv) => adv.advId === advId);
        if (isPm) {
          return advInfo.pmName;
        } else {
          return advInfo.bdName;
        }
      },
    },
  };
</script>

<template>
  <el-dialog
    :title="'Adv Payment Receipt'"
    :visible="confirmDialogVisible"
    :close-on-click-modal="false"
    width="1010px"
    @close="handleClose"
  >
    <el-alert
      v-show="isNotBlank(this.previewData.checkMessage)"
      :title="previewData.checkMessage"
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 20px"
    />
    <el-form
      label-position="left"
      label-width="200px"
      ref="baseForm"
      :model="formData"
      :rules="rules"
      :disabled="formProcessing"
    >
      <section class="grid-form">
        <el-form-item label="Receipt Claim Number" prop="advReceiptClaimNumber">
          <el-input v-model="formData.advReceiptClaimNumber">
            <template #append>
              <el-button
                icon="el-icon-search"
                :loading="formProcessing"
                @click="handleFetchDetail"
              />
            </template>
          </el-input>
        </el-form-item>
        <el-form-item
          v-if="receiptClaimStatus === 'CLAIMED'"
          label="Adv Company Platform"
          prop="advCompanyPlatform"
        >
          <el-select
            v-model="formData.advCompanyPlatform"
            filterable
            clearable
            placeholder="please select"
            @change="handleCompanyPlatformChange(formData.advCompanyPlatform)"
          >
            <el-option
              v-for="item in companyPlatformList"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-else label="Adv Company Platform">
          <span v-text="checkStrNA(previewData.advCompanyPlatform)" />
        </el-form-item>
        <el-form-item v-if="receiptClaimStatus === 'CLAIMED'" label="Adv Entity" prop="advEntity">
          <el-select
            v-model="formData.advEntity"
            ref="companyEntityOptions"
            filterable
            clearable
            placeholder="please select"
          >
            <el-option
              v-for="item in specificCompanyEntity"
              :key="item.id"
              :label="item.companyEntity"
              :value="item.companyEntity"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-else label="Adv Entity">
          <span>{{ checkStrNA(previewData.advEntity) }}</span>
        </el-form-item>
        <el-form-item label="Currency">
          <span>{{ checkStrNA(previewData.currency) }}</span>
        </el-form-item>
        <el-form-item label="HyperTap Entity">
          <span>{{ checkStrNA(getHyperTapCompanyAlias(previewData.hyperTapEntity)) }}</span>
        </el-form-item>
        <el-form-item label="Receipt Date">
          <span>{{ checkStrNA(previewData.receiptDate) }}</span>
        </el-form-item>
        <el-form-item label="Received Amount">
          <span>{{ checkNumNA(previewData.financeReceivedAmount) }}</span>
        </el-form-item>
        <el-form-item v-show="receiptType === 'POST_RECEIPT'" label="Receivable Amount">
          <span>{{ checkNumNA(previewData.financeReceivableAmount) }}</span>
        </el-form-item>
        <el-form-item v-show="receiptType === 'POST_RECEIPT'" label="Adv Revenue">
          <span>{{ checkNumNA(previewData.advRevenue) }}</span>
        </el-form-item>
        <el-form-item v-show="receiptType === 'POST_RECEIPT'" label="Adv Confirmed Amount">
          <span>{{ checkNumNA(previewData.advConfirmedAmount) }}</span>
        </el-form-item>
        <el-form-item v-show="receiptType === 'POST_RECEIPT'" label="Adv Hedge Amount">
          <span>{{ checkNumNA(previewData.advHedgeAmount) }}</span>
        </el-form-item>
      </section>

      <section style="padding: 0 10px" v-if="previewData.fileList.length > 0">
        <h3>Invoice Files</h3>
        <el-descriptions :column="1" border>
          <el-descriptions-item v-for="(item, index) in previewData.fileList" :key="index">
            <template slot="label">
              {{ item.filename }}
            </template>
            <el-link
              type="primary"
              target="_blank"
              :href="item.previewUrl.replace(/^http:\/\//i, 'https://')"
              :disabled="formProcessing"
              style="margin-right: 15px"
              >Preview
            </el-link>
            <el-link
              type="primary"
              :href="item.downloadUrl.replace(/^http:\/\//i, 'https://')"
              :disabled="formProcessing"
              >Download
            </el-link>
          </el-descriptions-item>
        </el-descriptions>
      </section>

      <el-divider
        v-if="receiptClaimStatus === 'CLAIMED' && receiptType === 'POST_RECEIPT'"
        content-position="left"
      />

      <section
        v-if="receiptClaimStatus === 'CLAIMED' && receiptType === 'POST_RECEIPT'"
        style="padding: unset"
      >
        <div v-for="(item, index) in formData.advertiserDateItems" :key="index">
          <line-frame
            :title="`Advertiser Info ${index + 1}`"
            :action="true"
            :add-action="true"
            :content-class="'grid-form-small'"
            @removeFrame="handleAdvertiserDateItem(index, true)"
            @addFrame="handleAdvertiserDateItem(index, false)"
          >
            <el-form-item label="Date" prop="recordDate">
              <el-date-picker
                v-model="item.recordDate"
                format="yyyy-MM"
                value-format="yyyy-MM"
                type="month"
                clearable
              />
            </el-form-item>
            <el-form-item label="Advertiser">
              <el-select v-model="item.advId" style="width: 220px" filterable>
                <el-option
                  v-for="item in previewData.advDetailList"
                  :key="item.advId"
                  :label="item.advName"
                  :value="item.advId"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="PM">
              <span
                v-text="
                  checkStrNA(
                    getPmBdNameFromAdvList(formData.advertiserDateItems[index].advId, true)
                  )
                "
              />
            </el-form-item>
            <el-form-item label="BD">
              <span
                v-text="
                  checkStrNA(
                    getPmBdNameFromAdvList(formData.advertiserDateItems[index].advId, false)
                  )
                "
              />
            </el-form-item>
          </line-frame>
        </div>
      </section>

      <el-divider content-position="left" />

      <section class="grid-form">
        <el-form-item label="Receipt Type" prop="receiptType">
          {{
            checkStrNA(
              getEnumLabel(
                advReceiptClaimReceiptTypeList,
                isNotBlank(this.previewData.receiptType)
                  ? this.previewData.receiptType
                  : receiptType
              )
            )
          }}
        </el-form-item>
        <el-form-item
          v-show="receiptType === 'POST_RECEIPT'"
          label="Receipt Status"
          prop="receiptStatus"
          :required="receiptClaimStatus === 'CLAIMED' && receiptType === 'POST_RECEIPT'"
        >
          <span v-if="receiptClaimStatus === 'CLAIM_CONFIRMED'">
            {{ checkStrNA(getEnumLabel(advFullReceiptStatusList, previewData.receiptStatus)) }}
          </span>
          <el-select v-else v-model="formData.receiptStatus" filterable>
            <el-option
              v-for="item in advReceiptClaimReceiptStatusList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          v-show="receiptType === 'PRE_RECEIPT'"
          label="Receipt Status"
          prop="receiptStatus"
        >
          <span>预收款</span>
        </el-form-item>
        <el-form-item v-if="receiptClaimStatus === 'CLAIMED'" label="Remark" prop="remark">
          <el-input
            v-model="formData.pmConfirmRemark"
            type="textarea"
            show-word-limit
            maxlength="255"
            placeholder="please input"
          />
        </el-form-item>
        <el-form-item v-if="receiptClaimStatus === 'CLAIM_CONFIRMED'" label="Remark" prop="remark">
          <el-input
            v-model="formData.pmAuditRemark"
            type="textarea"
            show-word-limit
            maxlength="255"
            placeholder="please input"
          />
        </el-form-item>
      </section>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button
        v-if="receiptClaimStatus === 'CLAIMED'"
        type="danger"
        :loading="formProcessing"
        @click="handleSubmit('CLAIM_REJECTED_FINANCE')"
        >驳回财务</el-button
      >
      <el-button
        v-if="receiptClaimStatus === 'CLAIMED'"
        type="primary"
        :loading="formProcessing"
        @click="handleSubmit('CLAIMED')"
        >认 领</el-button
      >
      <el-button
        v-if="receiptClaimStatus === 'CLAIM_CONFIRMED'"
        type="danger"
        :loading="formProcessing"
        @click="handleSubmit('CLAIM_REJECTED_PM')"
        >驳回认领</el-button
      >
      <el-button
        v-if="receiptClaimStatus === 'CLAIM_CONFIRMED'"
        type="success"
        :loading="formProcessing"
        @click="handleSubmit('CLAIM_CONFIRMED')"
        >认领确认</el-button
      >
    </span>
  </el-dialog>
</template>

<style scoped lang="scss"></style>
